import React, { useState, useEffect, useRef } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import SelectSymbol from '../../components/SelectSymbol/SelectSymbol';
import MyStrategies from './MyStrategies/MyStrategies';
import AiEvaluations from './AiEvaluations/AiEvaluations';
import AccountRiskReview from './AccountRiskReview/AccountRiskReview';
import SharedStrategies from './SharedStrategies/SharedStrategies';
import TemplateGallery from './StrategyTemplates/TemplateGallery';
import TemplateConfigModal from './StrategyTemplates/TemplateConfigModal';
import ImportStrategyModal from './ImportStrategyModal/ImportStrategyModal';
import { DEFAULT_SYMBOL } from '../../utils/SymbolUtils';
import { getEmptyMessage, getSuccessMessage } from '../../utils/MessageUtils';
import { getStoredValue, setStoredValue } from '../../utils/StorageUtils';
import { getStrategyTemplates } from '../../services/StrategiesService';
import BacktestButton from '../Automations/BacktestModal/BacktestButton';
import StrategyWizard from '../../components/StrategyWizard/StrategyWizard';
import BacktestModal from '../Automations/BacktestModal/BacktestModal';
import HowItWorks from '../../components/HowItWorks/HowItWorks';

function Strategies() {

    const [symbol, SetSymbol] = useState(loadStrategiesSymbol());
    const [notification, setNotification] = useState(getEmptyMessage());
    const [activeTab, setActiveTab] = useState('mine');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    // Ponte do Nova Estratégia → Setup AI Full: quando o usuário escolhe "vários ativos"
    // ou "automático" no fluxo manual (que só liga peças de 1 símbolo), abrimos o gerador
    // do AI Full já com o alvo pré-selecionado. aiFullTemplate é o descritor do template.
    const [aiFullTemplate, setAiFullTemplate] = useState(null);
    const [initialTargetMode, setInitialTargetMode] = useState(null);
    const aiFullTriggerRef = useRef(null);

    useEffect(() => {
        getStrategyTemplates()
            .then(list => setAiFullTemplate((list || []).find(t => t.id === 'AI_FULL') || null))
            .catch(() => { /* sem AI_FULL: a ponte fica inativa, o aviso textual continua */ });
    }, []);

    // Seleção pela galeria: alvo volta ao default (a ponte só pré-seleciona via onUseAiFull).
    function onGallerySelect(t) {
        setInitialTargetMode(null);
        setSelectedTemplate(t);
    }

    // Chamado pelo StrategyModal quando o alvo é "vários ativos"/"automático".
    function onUseAiFull(targetMode) {
        if (!aiFullTemplate) { setActiveTab('templates'); return; } // fallback: leva pra galeria
        setInitialTargetMode(targetMode);
        setSelectedTemplate(aiFullTemplate);
        // Atraso pra abrir só depois que o modal manual terminou de fechar — evita backdrop
        // preso do Bootstrap ao trocar de um modal pro outro.
        setTimeout(() => { if (aiFullTriggerRef.current) aiFullTriggerRef.current.click(); }, 350);
    }

    function loadStrategiesSymbol() {
        return getStoredValue('strategiesSymbol', DEFAULT_SYMBOL);
    }

    function saveStrategiesSymbol(value) {
        setStoredValue('strategiesSymbol', value);
    }

    function onSymbolChange(event) {
        saveStrategiesSymbol(event.target.value);
        SetSymbol(event.target.value);
    }

    function onTemplateApplied(strategy) {
        setNotification(getSuccessMessage(`Estratégia "${strategy.name}" criada com sucesso! Revise os componentes e ative quando estiver pronto.`));
        setActiveTab('mine');
        setRefreshKey(k => k + 1);
    }

    function onImportSuccess(strategy) {
        setNotification(getSuccessMessage(`Estratégia "${strategy.name}" importada com sucesso!`));
        setActiveTab('mine');
        setRefreshKey(k => k + 1);
    }

    return (
        <React.Fragment>
            <Menu />
            <main className='content'>
                <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4'>
                    <div className='d-block mb-4 mb-md-0'>
                        <h2 className='h4'>Estratégias</h2>
                    </div>
                    <div className='btn-toolbar mb-2 mb-md-0'>
                        <div className='d-inline-flex align-items-center'>
                            <div className='me-2'><StrategyWizard /></div>
                            <BacktestButton />
                            <button className='btn btn-outline-secondary animate-up-2 me-2' onClick={() => setActiveTab('templates')}>
                                ✨ Usar template
                            </button>
                            <button className='btn btn-outline-secondary animate-up-2 me-2' data-bs-toggle='modal' data-bs-target='#modalImportStrategy'>
                                Importar
                            </button>
                            <div className='me-2'>
                                <SelectSymbol symbol={symbol} disabled={false} onChange={onSymbolChange} includeAllOption={true} />
                            </div>
                            <button id='btnNewStrategy' className='btn btn-primary animate-up-2' data-bs-toggle='modal' data-bs-target='#modalStrategy'>
                                <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" /></svg>
                                Nova Estratégia
                            </button>
                        </div>
                    </div>
                </div>

                <ul className='nav nav-tabs mb-3' role='tablist'>
                    <li className='nav-item'>
                        <button className={`nav-link ${activeTab === 'mine' ? 'active' : ''}`} onClick={() => setActiveTab('mine')}>
                            Minhas Estratégias
                        </button>
                    </li>
                    <li className='nav-item'>
                        <button className={`nav-link ${activeTab === 'shared' ? 'active' : ''}`} onClick={() => setActiveTab('shared')}>
                            Compartilhadas
                        </button>
                    </li>
                    <li className='nav-item'>
                        <button className={`nav-link ${activeTab === 'templates' ? 'active' : ''}`} onClick={() => setActiveTab('templates')}>
                            ✨ Templates
                        </button>
                    </li>
                </ul>

                <HowItWorks>
                    <p className='mb-1'>Uma <strong>Estratégia</strong> agrupa Monitor + Automação de Compra + Automação de Venda em um único componente ativável.</p>
                    <ul className='mb-0 ps-3'>
                        <li>Ativar a estratégia inicia todos os componentes de uma vez.</li>
                        <li>Use a aba <strong>✨ Templates</strong> para criar uma estratégia completa automaticamente — sem precisar configurar cada peça manualmente.</li>
                        <li>Estratégias compartilhadas podem ser copiadas e adaptadas para o seu perfil.</li>
                    </ul>
                    <p className='mt-2 mb-0 text-warning'>Recomendado para iniciantes: comece pelo Template "DCA com Filtro de Regime" ou "EMA Crossover".</p>
                </HowItWorks>

                {activeTab === 'mine' && (
                    <>
                        <AiEvaluations onApplied={() => setRefreshKey(k => k + 1)} setNotification={setNotification} />
                        <AccountRiskReview setNotification={setNotification} />
                        <MyStrategies key={refreshKey} symbol={symbol} onNotification={setNotification} onUseAiFull={onUseAiFull} />
                    </>
                )}
                {activeTab === 'shared' && (
                    <SharedStrategies symbol={symbol} onNotification={setNotification} />
                )}
                {activeTab === 'templates' && (
                    <div className='card card-body border-0 shadow mb-4'>
                        <TemplateGallery onSelect={onGallerySelect} />
                    </div>
                )}

                {/* Trigger oculto: abre o #modalTemplateConfig a partir da ponte (onUseAiFull). */}
                <button ref={aiFullTriggerRef} type='button' className='d-none' data-bs-toggle='modal' data-bs-target='#modalTemplateConfig' aria-hidden='true'></button>
                <TemplateConfigModal template={selectedTemplate} onApplied={onTemplateApplied} initialTargetMode={initialTargetMode} />
                <ImportStrategyModal onSuccess={onImportSuccess} />
                <BacktestModal showStrategies={true} />
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    )
}

export default Strategies;
